import { ICollectionResponse, ISearchRequestObject } from '@logz-build/typescript';
import { CrudApiService } from '../utilities/crud.api.service';
import { handleDalError } from '../utilities/utilities';
import {
  ISawmillPipeline,
  ISawmillPipelinePreviewRequest,
  ISawmillPipelineSearchResponse,
  ISawmillPipelineValidationResponse,
} from './types';

export class SawmillPipelineApiService {
  crudApiService = new CrudApiService<ISawmillPipelineSearchResponse, ISawmillPipeline>();

  urls = {
    search: '/sawmill/pipelines/search',
    activate: (id: string) => `/sawmill/pipelines/${id}/activate`,
    deactivate: (id: string) => `/sawmill/pipelines/${id}/deactivate`,
    restore: (id: string) => `/sawmill/pipelines/${id}/restore`,
    create: '/sawmill/pipelines',
    update: (id: string) => `/sawmill/pipelines/${id}`,
    view: (id: string) => `/sawmill/pipelines/${id}`,
    delete: (id: string) => `/sawmill/pipelines/${id}`,
    validate: `/sawmill/pipeline-validation`,
    preview: (logType: string) => `/sawmill/log-type-pipeline/${logType}`,
  };

  search = async (
    searchRequest: ISearchRequestObject<{ logType: string; name: string; status: boolean; date: number }>,
  ): Promise<ICollectionResponse<ISawmillPipelineSearchResponse>> => {
    return this.crudApiService.search(this.urls.search, searchRequest);
  };

  getOne = async (id: string): Promise<ISawmillPipeline> => {
    return this.crudApiService.get(this.urls.view(id));
  };

  activate = async (id: string): Promise<void> => {
    return this.crudApiService.do(this.urls.activate(id));
  };

  deactivate = async (id: string): Promise<void> => {
    return this.crudApiService.do(this.urls.deactivate(id));
  };

  restore = async (id: string): Promise<void> => {
    return this.crudApiService.do(this.urls.restore(id));
  };

  create = async (model: ISawmillPipeline): Promise<ISawmillPipeline> => {
    return this.crudApiService.create(this.urls.create, model) as unknown as Promise<ISawmillPipeline>;
  };

  update = async (id: string, rule: ISawmillPipeline): Promise<ISawmillPipeline> => {
    return this.crudApiService.do(this.urls.update(id), { payload: rule }) as unknown as Promise<ISawmillPipeline>;
  };

  remove = async (id: string): Promise<void> => {
    await this.crudApiService.delete(this.urls.delete(id));
  };

  validate = async (rule: ISawmillPipeline): Promise<ISawmillPipelineValidationResponse> => {
    return this.crudApiService.do(this.urls.validate, { payload: rule });
  };

  preview = async (logType: string, previewRequest: ISawmillPipelinePreviewRequest): Promise<string[]> => {
    const response = await this.crudApiService.do<string[]>(this.urls.preview(logType), {
      payload: previewRequest,
    });

    try {
      const parsedResponse = response.map(item => JSON.parse(item));

      return parsedResponse;
    } catch (error) {
      handleDalError(error);
    }
  };
}

export const sawmillPipelineApiService = new SawmillPipelineApiService();
